import { useMemo } from 'react';

import {
  AssetDTO,
  EntityRefUtil,
  FormDTO,
  FormElement,
  FormElementKind,
  FormRevisionDiff,
  FormRevisionListItemDTO,
  FormType,
  RecordState,
  TableFormElement,
} from '@gearbelt/global';
import { useQuery } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';

import { initializeTableInitialValues } from '@/components/form/elements/table';
import { FormElementsRenderer, FormRendererData } from '@/components/form/form-elements-renderer';
import CloseIcon from '@/components/icons/close';
import { Dialog, DialogClose, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { getAssetsQueryOptions } from '@/queries/assets';
import { useIsAWA } from '@/utils/awa';
import { compareFormRevisions, filterDiffableElements, splitDiffForColumns } from '@/utils/form-revision-diff.util';

function buildPreviewDefaults(elements: FormElement[]): FormRendererData {
  return elements.reduce(
    (acc, element) => ({
      ...acc,
      ...(element.kind === FormElementKind.Table
        ? { [element.id]: [[...initializeTableInitialValues(element as TableFormElement)]] }
        : { [element.id]: undefined }),
    }),
    {} as FormRendererData,
  );
}

function formTypeLabel(type: number): string {
  switch (type) {
    case FormType.InspectionForm:
      return 'Inspection Form';
    case FormType.RepairForm:
      return 'Repair Form';
    case FormType.ScheduleForm:
      return 'Schedule Form';
    default:
      return 'Form Template';
  }
}

function RevisionColumn({
  heading,
  version,
  form,
  asset,
  elements,
  decorations,
}: {
  heading: string;
  version: number;
  form: FormDTO;
  asset: AssetDTO;
  elements: FormElement[];
  decorations: FormRevisionDiff;
}) {
  const formInstance = useForm<FormRendererData>({
    defaultValues: buildPreviewDefaults(elements),
  });

  return (
    <div className="min-w-0 flex-1 basis-0">
      <div className="mb-2 text-center text-lg font-medium">{heading}</div>
      <div className="mb-4 flex flex-col gap-0.5 text-sm">
        <div>
          <span className="font-bold">Form Name:</span> {form.name}
        </div>
        <div>
          <span className="font-bold">Form ID:</span> {EntityRefUtil.createFormRef(form.number)}
        </div>
        <div>
          <span className="font-bold">Form Version:</span> {version}
        </div>
        <div>
          <span className="font-bold">Form Type:</span> {formTypeLabel(form.type)}
        </div>
      </div>
      <FormProvider {...formInstance}>
        <div className="flex flex-col gap-4">
          <FormElementsRenderer
            form={form}
            asset={asset}
            elements={elements}
            elementDecorations={decorations}
            readonly
            isPreview
            showConfigSummary
            applyVisibilityFilter={false}
          />
        </div>
      </FormProvider>
    </div>
  );
}

export function FormRevisionDiffModal({
  open,
  onClose,
  form,
  previous,
  current,
}: {
  open: boolean;
  onClose: () => void;
  form: FormDTO;
  previous: FormRevisionListItemDTO;
  current: FormRevisionListItemDTO;
}) {
  const { isAWA } = useIsAWA();
  const { data: assets = [], isLoading: isLoadingAssets } = useQuery(getAssetsQueryOptions({ limit: 1, isAWA, filters: { state: RecordState.Active } }));
  const placeholderAsset = assets[0] ?? null;

  const { beforeElements, afterElements, beforeDiff, afterDiff } = useMemo(() => {
    const beforeElements = filterDiffableElements(previous.elements, form.type);
    const afterElements = filterDiffableElements(current.elements, form.type);
    const diff = compareFormRevisions(beforeElements, afterElements);
    const { before, after } = splitDiffForColumns(diff);
    return { beforeElements, afterElements, beforeDiff: before, afterDiff: after };
  }, [previous.elements, current.elements, form.type]);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="top-[6.5vh] h-[95vh] max-h-[95vh] w-[98.5vw] max-w-none px-0">
        <div className="flex items-center justify-between px-4">
          <DialogTitle hideCloseButton>
            <span className="text-xl font-medium">Form activity comparison:</span>
          </DialogTitle>
          <DialogClose aria-label="Close" className="cursor-pointer">
            <CloseIcon className="h-5 w-5" />
          </DialogClose>
        </div>

        <div className="border-b" />

        {isLoadingAssets ? (
          <div className="text-secondary-text bg-grey-1 rounded-md p-4 text-center text-sm">Loading…</div>
        ) : !placeholderAsset ? (
          <div className="text-secondary-text bg-grey-1 rounded-md p-4 text-center text-sm">No assets available to preview this form.</div>
        ) : (
          <div className="flex min-w-0 gap-6 px-4">
            <RevisionColumn
              heading="Before:"
              version={previous.revision + 1}
              form={form}
              asset={placeholderAsset}
              elements={beforeElements}
              decorations={beforeDiff}
            />
            <RevisionColumn
              heading="After:"
              version={current.revision + 1}
              form={form}
              asset={placeholderAsset}
              elements={afterElements}
              decorations={afterDiff}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
